import { pb, escapeFilterValue } from '../lib/pocketbase'
import { throwApiError, catch404 } from '../utils/apiErrorHandler'
import { logger } from '../utils/logger'
import type { Invitation, InvitationRole } from '../types'
import { APP_URL, APP_NAME, NOREPLY_EMAIL } from '../lib/config'

/**
 * Default seat limits per subscription tier (when agency.seat_limit is not set).
 *
 * The current tier names were missing here, so every agency on Independent,
 * Practice, Agency or Institution fell through to the `|| 3` default — an
 * Institution account, sold as unlimited agents, could invite two people. The
 * table only knew the legacy names, and the Aug 2026 rename made every new
 * value miss it.
 *
 * Legacy names are deliberately NOT resolved through lib/plans.ts here, unlike
 * everywhere else. That map exists so a legacy value RENDERS under its current
 * name, which is safe. Applying it to entitlements is not: `starter` forwards
 * to Independent, and Independent is one seat where starter was three, so
 * resolving would silently cut two seats from every existing starter agency and
 * lock out users who are already in the account. Legacy rows keep the number
 * they were sold; only new tiers use the new numbers.
 */
const UNLIMITED_SEATS = Number.MAX_SAFE_INTEGER

const DEFAULT_SEAT_LIMITS: Record<string, number> = {
  // Current tiers — these match the agent counts published on /pricing.
  independent: 1,
  practice: 3,
  agency: 8,
  institution: UNLIMITED_SEATS,
  // Legacy tiers — frozen at the limits those customers were sold.
  starter: 3,
  pro: 10,
  team: 25,
  enterprise: UNLIMITED_SEATS,
}

/**
 * Check if the agency has room for another user/invitation.
 * Counts existing users + pending (unaccepted) invitations against the seat limit.
 * Superadmins bypass this check.
 */
export async function checkSeatLimit(agencyId: string): Promise<{
  allowed: boolean
  currentSeats: number
  maxSeats: number
  message?: string
}> {
  // Fetch the agency to get seat_limit and plan
  const agency = await pb.collection('agencies').getOne(agencyId)
  const tier = (agency as any).subscription_tier || 'starter'
  const additionalSeats = Number((agency as any).additional_seats) || 0
  // A PocketBase number field defaults to 0, not null, so `??` never reached the
  // tier default — every agency read as a limit of 0 and no invitation could ever
  // be sent. Only a positive stored value counts as a configured limit.
  const storedLimit = Number((agency as any).seat_limit)
  const seatLimit = storedLimit > 0 ? storedLimit : (DEFAULT_SEAT_LIMITS[tier] || 3)
  const maxSeats = seatLimit + additionalSeats

  // Count active users in this agency
  const users = await pb.collection('users').getList(1, 1, {
    filter: `agency_id = "${escapeFilterValue(agencyId)}"`,
  })
  const userCount = users.totalItems

  // Count pending (unaccepted) invitations for this agency
  const pendingInvites = await pb.collection('invitations').getList(1, 1, {
    filter: `agency_id = "${escapeFilterValue(agencyId)}" && accepted = false`,
  })
  const pendingCount = pendingInvites.totalItems

  const currentSeats = userCount + pendingCount

  if (currentSeats >= maxSeats) {
    return {
      allowed: false,
      currentSeats,
      maxSeats,
      message: `Seat limit reached (${currentSeats}/${maxSeats}). Upgrade your plan or purchase additional seats to invite more team members.`,
    }
  }

  return { allowed: true, currentSeats, maxSeats }
}

export type InvitationPayload = {
  email: string
  agency_id: string
  role: InvitationRole
}

/**
 * Generate a cryptographically secure random token for invitations
 * Note: PocketBase auto-generates tokens with pattern [a-zA-Z0-9]{50}
 * This is a fallback if we need to generate one manually
 */
function generateToken(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
  const randomValues = new Uint32Array(50)
  crypto.getRandomValues(randomValues)
  let token = ''
  for (let i = 0; i < 50; i++) {
    token += chars.charAt(randomValues[i] % chars.length)
  }
  return token
}

function mapInvitation(record: Record<string, any>): Invitation {
  return {
    id: record.id,
    email: record.email ?? '',
    agency_id: record.agency_id ?? '',
    role: record.role ?? 'agent',
    token: record.token ?? '',
    accepted: record.accepted ?? false,
    invited_by: record.invited_by ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
    expand: record.expand,
    // Legacy aliases
    invitation_code: record.token ?? '',
    used: record.accepted ?? false,
    created_by: record.invited_by ?? '',
  }
}

/**
 * List all invitations for the current user's agency
 */
export async function listInvitations(): Promise<Invitation[]> {
  try {
    const user = pb.authStore.model
    if (!user) {
      throw new Error('You must be signed in to view invitations')
    }

    const isSuperAdmin = (user as any).role === 'superadmin'
    const userAgencyId = (user as any).agency_id
    if (!isSuperAdmin && !userAgencyId) {
      throw new Error('You must be assigned to an agency to view invitations')
    }

    // Superadmins see all invitations, regular users see only their agency's
    const filter = isSuperAdmin ? '' : `agency_id = "${escapeFilterValue(userAgencyId)}"`
    const records = await pb.collection('invitations').getFullList<Record<string, any>>({
      filter,
      sort: '-created',
      expand: 'agency_id,invited_by'
    })

    return records.map(mapInvitation)
  } catch (err: any) {
    throwApiError(err, 'Failed to list invitations')
  }
}

/**
 * Create a new invitation
 */
export async function createInvitation(payload: InvitationPayload): Promise<Invitation & { emailError: string | null }> {
  try {
    const user = pb.authStore.model
    if (!user) {
      throw new Error('You must be signed in to create invitations')
    }

    const userRole = (user as any).role
    const isSuperAdmin = (user as any).role === 'superadmin'
    if (!isSuperAdmin && userRole !== 'admin') {
      throw new Error('Only admins can create invitations')
    }

    const userAgencyId = (user as any).agency_id
    if (!userAgencyId) {
      throw new Error('You must be assigned to an agency to create invitations')
    }

    // Check if user is super admin or creating for their own agency
    if (!isSuperAdmin && payload.agency_id !== userAgencyId) {
      throw new Error('You can only create invitations for your own agency')
    }

    // Enforce seat limit (superadmins bypass)
    if (!isSuperAdmin) {
      const seatCheck = await checkSeatLimit(payload.agency_id)
      if (!seatCheck.allowed) {
        throw new Error(seatCheck.message!)
      }
    }

    // Generate unique token
    let token = generateToken()
    let tokenExists = true
    let attempts = 0

    // Ensure token is unique (retry if collision)
    while (tokenExists && attempts < 10) {
      const existing = await catch404(
        pb.collection('invitations').getFirstListItem(`token = "${escapeFilterValue(token)}"`)
      )
      if (existing) {
        // Token exists, generate new one
        token = generateToken()
        attempts++
      } else {
        tokenExists = false
      }
    }

    if (tokenExists) {
      throw new Error('Failed to generate unique invitation token. Please try again.')
    }

    const record = await pb.collection('invitations').create({
      email: payload.email.toLowerCase().trim(),
      token: token,
      agency_id: payload.agency_id,
      role: payload.role,
      invited_by: user.id,
      accepted: false
    })

    logger.debug(`Created invitation for ${payload.email} to agency ${payload.agency_id}`)

    const invitation = mapInvitation(record)

    // The row exists and holds a seat whether or not the email goes. The
    // caller is told which, with the reason, instead of a blanket "sent".
    let emailError: string | null = null
    try {
      const agency = await pb.collection('agencies').getOne(payload.agency_id)
      await sendInvitationEmail(invitation, agency.agency_name)
    } catch (emailErr: any) {
      emailError = emailErr?.message || 'Email failed to send'
      logger.error('Invitation created but email failed to send:', emailErr)
    }

    return { ...invitation, emailError }
  } catch (err: any) {
    throwApiError(err, 'Failed to create invitation')
  }
}

/**
 * Get invitation by token (for registration)
 */
export async function getInvitationByToken(token: string): Promise<Invitation | null> {
  try {
    const record = await pb.collection('invitations').getFirstListItem<Record<string, any>>(
      `token = "${escapeFilterValue(token)}"`,
      { expand: 'agency_id,invited_by' }
    )

    return mapInvitation(record)
  } catch (err: any) {
    if (err.status === 404) {
      return null
    }
    throwApiError(err, 'Failed to get invitation')
    return null // Unreachable but TypeScript needs this
  }
}

/**
 * Get invitation by code (legacy alias for getInvitationByToken)
 */
export async function getInvitationByCode(code: string): Promise<Invitation | null> {
  return getInvitationByToken(code)
}

/**
 * Delete an invitation
 */
export async function deleteInvitation(invitationId: string): Promise<void> {
  try {
    const user = pb.authStore.model
    if (!user) {
      throw new Error('You must be signed in to delete invitations')
    }

    const userRole = (user as any).role
    if (userRole !== 'admin' && userRole !== 'superadmin') {
      throw new Error('Only admins can delete invitations')
    }

    await pb.collection('invitations').delete(invitationId)
    logger.debug(`Deleted invitation ${invitationId}`)
  } catch (err: any) {
    throwApiError(err, 'Failed to delete invitation')
  }
}

/**
 * Mark invitation as accepted
 */
export async function acceptInvitation(invitationId: string): Promise<Invitation> {
  try {
    const record = await pb.collection('invitations').update(invitationId, {
      accepted: true
    })

    logger.debug(`Invitation ${invitationId} accepted`)
    return mapInvitation(record)
  } catch (err: any) {
    throwApiError(err, 'Failed to accept invitation')
  }
}

/**
 * Generate the invite URL for an invitation
 */
export function getInviteUrl(token: string): string {
  const baseUrl = APP_URL || window.location.origin
  // /register is the paid-signup page and does not understand invitations,
  // and /onboarding is behind sign-in. /login is public: it keeps the code
  // across the sign-in round trip and lands on onboarding with it filled in.
  return `${baseUrl}/login?invite=${token}`
}

/**
 * Generate email content for an invitation
 */
export function generateInviteEmailContent(invitation: {
  email: string
  token: string
  role: string
  agency_name?: string
}): { subject: string; body: string; mailto: string } {
  const inviteUrl = getInviteUrl(invitation.token)
  const agencyName = invitation.agency_name || 'your agency'

  const subject = `You're invited to join ${agencyName} on ${APP_NAME}`

  const body = `Hi,

You've been invited to join ${agencyName} on ${APP_NAME} as a ${invitation.role}.

Click the link below to create your account:
${inviteUrl}

This invitation is for: ${invitation.email}

If you have any questions, please contact your agency administrator.

Best regards,
The ${APP_NAME} Team`

  const mailto = `mailto:${invitation.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`

  return { subject, body, mailto }
}

/**
 * Send invitation email via Resend
 */
async function sendInvitationEmail(invitation: Invitation, agencyName: string): Promise<void> {
  const inviteUrl = getInviteUrl(invitation.token)
  const subject = `You're invited to join ${agencyName} on ${APP_NAME}`
  const htmlBody = `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #333; line-height: 1.6; margin: 0; padding: 0;">
  <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
    <div style="background: linear-gradient(135deg, #C8A45C 0%, #7A6430 100%); color: white; padding: 30px; border-radius: 0; text-align: center;">
      <h1 style="margin: 0;">${APP_NAME}</h1>
      <p style="margin: 10px 0 0; opacity: 0.9;">You've been invited!</p>
    </div>
    <div style="background: #f8f9fa; padding: 30px; border: 1px solid #e9ecef;">
      <p>You've been invited to join <strong>${agencyName}</strong> on ${APP_NAME} as a <strong>${invitation.role}</strong>.</p>
      <p>Click the button below to create your account:</p>
      <div style="text-align: center; margin: 24px 0;">
        <a href="${inviteUrl}" style="display: inline-block; background: #C8A45C; color: white; padding: 14px 28px; border-radius: 0; text-decoration: none; font-weight: 600;">Accept Invitation</a>
      </div>
      <p style="font-size: 0.875rem; color: #666;">Or copy this link: <a href="${inviteUrl}" style="color: #C8A45C; word-break: break-all;">${inviteUrl}</a></p>
      <p style="font-size: 0.875rem; color: #666;">This invitation is for: ${invitation.email}</p>
    </div>
    <div style="background: #f1f3f5; padding: 20px; border-radius: 0; text-align: center; font-size: 0.75rem; color: #666;">
      <p style="margin: 0;">${APP_NAME} — Sports Agency Management</p>
    </div>
  </div>
</body>
</html>`

  const response = await fetch('/api/send-email', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: JSON.stringify({
      to: invitation.email,
      subject,
      body: htmlBody,
      from: NOREPLY_EMAIL,
      fromName: APP_NAME,
    }),
  })
  if (!response.ok) {
    const text = await response.text()
    throw new Error(`Email send failed: ${text}`)
  }
}

/**
 * Resend invitation email via Resend
 */
export async function resendInvitation(invitationId: string): Promise<{ email: string; inviteUrl: string }> {
  try {
    const invitation = await pb.collection('invitations').getOne(invitationId, { expand: 'agency_id' })
    const agencyName = (invitation.expand as any)?.agency_id?.agency_name
    if (!agencyName) throw new Error('The invitation is not attached to an agency.')
    await sendInvitationEmail(mapInvitation(invitation), agencyName)
    logger.debug(`Resent invite email for ${invitation.email}`)
    return { email: invitation.email, inviteUrl: getInviteUrl(invitation.token) }
  } catch (err: any) {
    throwApiError(err, 'Failed to resend invitation email')
  }
}

/**
 * Copy invite link to clipboard
 */
export async function copyInviteLink(token: string): Promise<void> {
  const inviteUrl = getInviteUrl(token)
  await navigator.clipboard.writeText(inviteUrl)
}

/**
 * Accept an invitation as the signed-in invitee, via the serverless endpoint.
 *
 * This cannot be a direct PocketBase call: the invitations collection is
 * readable only by admins of the owning agency, so the invitee can neither
 * look their invitation up nor mark it accepted. The endpoint does both with
 * admin credentials after verifying the caller's token, the invited email and
 * the 7-day expiry.
 */
export async function acceptInvitationByToken(token: string): Promise<{
  agencyId: string
  agencyName: string | null
  role: InvitationRole
}> {
  const res = await fetch('/api/accept-invite', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${pb.authStore.token}`,
    },
    body: JSON.stringify({ token }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data.error || 'Failed to accept the invitation')
  }
  return data
}
